/**
 * 房间信息
 */
import React, { useMemo, useRef, memo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Table, Button, Alert } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import ContentBlock from '@/components/Reservation/ContentBlock';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import CardManage from '@/components/Reservation/RoomCardManage';
import CouponSelector, { CouponSelectorRef } from '@/components/Reservation/CouponSelector';
import CouponGapText from '@/components/Reservation/CouponGapText';
import ExchangeRadio from '@/pages/reservation/roomExchange/components/ExchangeRadio';
import {
  RoomTypeSelector,
  RoomNoSelector,
} from '@/pages/reservation/roomExchange/components/RoomSelector';
import { getExchangeActionType, ExchangeActionHint } from '@/pages/reservation/roomExchange/helper';
import { RESERVATION_SCENE, SourceChannelType } from '@/config/constant';
import { format2TimeStamp } from '@/utils/date';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import {
  createStateSelector,
  findItemByKey,
  getEmptyCouponData,
  flatPriceList,
  getRoomCheckInStatus,
} from '@/redux/actions/reservation/helper';
import {
  handleRentItemChange,
  handleExchangeTypeChange,
  handleTargetRoomTypeChange,
  batchUpdateRoomCoupon,
} from '@/redux/actions/reservation/checkInExchange';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkInExchange';
import {
  CouponValue,
  ExchangeRentRoomInfo,
  ExchangeRoomPriceInfo,
  ExchangeRoomRole,
  RoomExchangeType,
} from '@/redux/reducers/reservation/base.d';
import CouponService from '@/components/Reservation/CouponService';
import useUpdate from '@/hooks/useUpdate';
import BatchCouponButton, {
  IBatchCouponParams,
} from '@/components/Reservation/BatchSelectCouponButton';

import { getPopupContainer } from '../../helper';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const {
    rentRoomInfos,
    commonInfo,
    rentTimeInfo,
    allRoomList,
    marketPriceList,
    cardEnabled,
    peripheralCount,
  } = state;
  return {
    rentRoomInfos,
    allRoomList,
    commonInfo,
    rentTimeInfo,
    marketPriceList,
    cardEnabled,
    peripheralCount,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callRentItemChange: handleRentItemChange,
  callExchangeTypeChange: handleExchangeTypeChange,
  callTargetTypeChange: handleTargetRoomTypeChange,
  callBatchUpdateCoupon: batchUpdateRoomCoupon,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;
interface IProps {
  refreshRoomCardCount: () => void;
}

const Index: React.FC<IProps & MapStateProps & MapDispatchProps> = (props) => {
  const {
    rentRoomInfos,
    allRoomList,
    commonInfo,
    rentTimeInfo,
    marketPriceList,
    cardEnabled,
    peripheralCount,
    callRentItemChange,
    callTargetTypeChange,
    callExchangeTypeChange,
    callBatchUpdateCoupon,
    refreshRoomCardCount,
  } = props;

  const couponSelectorRef = useRef<CouponSelectorRef>({
    openModal: () => {},
    closeModal: () => {},
  });
  const cardManageRef = useRef<{ showModal: (...arg) => void }>({ showModal: () => {} });

  const { exchangeType, ownerWYId } = commonInfo;
  const isSameType = exchangeType === RoomExchangeType.KeepSame; // 是否是同价换房

  const forceUpdate = useUpdate();

  const couponService = useMemo(() => {
    return new CouponService();
  }, []);

  useEffect(() => {
    // 获取当前用户可用券
    couponService.initCouponList(ownerWYId).then(forceUpdate);
  }, [ownerWYId]);

  // 更新券状态
  const updateCouponService = () => {
    try {
      // 恢复券状态
      couponService.resetWYListStatus();
      const unlockList = [];
      const lockList = [];
      rentRoomInfos.forEach((roomInfo) => {
        (roomInfo.roomPriceList || []).forEach((priceInfo) => {
          const itemNo = priceInfo.couponNo;
          if (!itemNo) return;
          if (roomInfo.itemRole === ExchangeRoomRole.OriginRoom) {
            // 原房间的券 需要解锁
            unlockList.push(itemNo);
          }
          if (roomInfo.itemRole === ExchangeRoomRole.TargetRoom) {
            // 新房间的券 需要锁定
            lockList.push(itemNo);
          }
        });
      });
      // 解锁原房间卷
      couponService.batchUnlockWYCoupon(unlockList);
      // 锁定新房间券
      couponService.batchLockWYCoupon(lockList);
    } catch (e) {
      //
    }
  };
  updateCouponService();

  // 房间类型
  const renderRoomTag: (id: string, record: ExchangeRentRoomInfo) => string = (id, record) => {
    return {
      [ExchangeRoomRole.OriginRoom]: intl.get('drawer_exchange_room_old_symbol'),
      [ExchangeRoomRole.TargetRoom]: intl.get('drawer_exchange_room_new_symbol'),
    }[record.itemRole];
  };

  // 新房间改变房型
  const handleRoomTypeChange: (roomTypeId: number) => void = (roomTypeId) => {
    const targetType = findItemByKey(allRoomList, 'id', roomTypeId);
    if (targetType) {
      callTargetTypeChange(targetType.id);
    }
  };

  const exchangeHintText = useMemo(() => {
    return ExchangeActionHint[getExchangeActionType(rentRoomInfos, flatPriceList(marketPriceList))];
  }, [rentRoomInfos, marketPriceList]);

  // 房间类型
  const renderRoomType: (roomTypeId: number, record: ExchangeRentRoomInfo) => React.ReactNode = (
    roomTypeId,
    record,
  ) => {
    // 原房间 只读
    if (record.itemRole === ExchangeRoomRole.OriginRoom) return record.roomTypeName;
    return (
      <div>
        <RoomTypeSelector
          allRoomList={allRoomList}
          value={roomTypeId}
          getPopContainer={getPopupContainer}
          onChange={(value) => handleRoomTypeChange(value)}
        />
        <div className={styles.exchangeHintText}>{exchangeHintText}</div>
      </div>
    );
  };

  // 房间改变
  const handleRoomIdChange: (roomId: number, record: ExchangeRentRoomInfo) => void = (
    roomId,
    record,
  ) => {
    const { roomTypeId, indexId } = record;
    const category = findItemByKey(allRoomList, 'id', roomTypeId);
    const roomItem = findItemByKey(category?.roomList, 'id', roomId);
    if (roomItem && getRoomCheckInStatus(roomItem)) {
      callRentItemChange(indexId, {
        roomId: roomItem.id,
        roomNo: roomItem.roomNo,
      });
    }
  };

  // 房间号
  const renderRoomNo: (roomId: number, record: ExchangeRentRoomInfo) => React.ReactNode = (
    roomId,
    record,
  ) => {
    const originRoom = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom);
    if (record.itemRole === ExchangeRoomRole.OriginRoom)
      return record.roomNo || intl.get('app_reservation_room_queue_no');

    return (
      <RoomNoSelector
        typeId={record.roomTypeId}
        value={roomId}
        allRoomList={allRoomList}
        getPopContainer={getPopupContainer}
        isReserve={false}
        originRoomId={originRoom?.roomId}
        onChange={(value) => handleRoomIdChange(value, record)}
      />
    );
  };

  const handlePriceChange: (
    text: string,
    dayPrice: ExchangeRoomPriceInfo,
    record: ExchangeRentRoomInfo,
  ) => void = (text, dayPrice, record) => {
    // if (PRICE_REG.test(text)) {
    const { rentDateId } = dayPrice;
    callRentItemChange(record.indexId, {
      roomPriceList: (record.roomPriceList || []).map((item) => {
        if (item.rentDateId === rentDateId) {
          return {
            ...item,
            // 价格修改 删除已选券
            ...getEmptyCouponData(),
            inputPrice: text,
          };
        }
        return item;
      }),
    });
    // }
  };

  const renderCardNum = (val, record) => {
    if (record.itemRole === ExchangeRoomRole.OriginRoom) return val;
    return '—';
  };

  // 押金
  const handleDepositChange: (deposit: string, record: ExchangeRentRoomInfo) => void = (
    deposit,
    record,
  ) => {
    // if (PRICE_REG.test(deposit)) {
    callRentItemChange(record.indexId, { deposit });
    // }
  };

  const showCouponSelector: (
    dayPrice: ExchangeRoomPriceInfo,
    record: ExchangeRentRoomInfo,
  ) => void = (dayPrice, record) => {
    if (record.itemRole === ExchangeRoomRole.OriginRoom) return; // 原房间不可修改
    const { inputPrice, couponNo, rentDateId } = dayPrice;
    const unlockCouponList = []; // 解锁的券列表
    const lockCouponList = []; // 锁定的券列表
    rentRoomInfos.forEach((roomInfo) => {
      (roomInfo.roomPriceList || []).forEach((priceInfo) => {
        const itemNo = priceInfo.couponNo;
        if (!itemNo) return;
        if (roomInfo.itemRole === ExchangeRoomRole.OriginRoom) {
          // 解锁原房间的优惠券
          unlockCouponList.push(itemNo);
        }
        if (roomInfo.itemRole === ExchangeRoomRole.TargetRoom) {
          // 锁定新房间的优惠券
          lockCouponList.push(itemNo);
        }
      });
    });
    couponSelectorRef.current.openModal({
      wyuid: ownerWYId,
      sceneType: RESERVATION_SCENE.CheckInExchange,
      amount: parseYuan2Fen(inputPrice),
      occType: rentTimeInfo.occType,
      changedCouponId: couponNo ? [couponNo] : [],
      lockCouponList,
      unlockCouponList,
      channelType: dayPrice.priceChannel,
      businessDate: dayPrice.rentDate,
      callback: (_, infoList) => {
        // 更新手工券
        couponService.refreshManualList().then(forceUpdate);
        const [targetDetail] = infoList;
        let updateCouponInfo: CouponValue = getEmptyCouponData();
        if (targetDetail) {
          const { couponFaceValue, couponName, couponType, wyuid, couponSubType } = targetDetail;
          updateCouponInfo = {
            couponValue: parseFen2Yuan(couponFaceValue || 0),
            couponAmount: couponFaceValue,
            couponName,
            couponNo: targetDetail.couponNo,
            couponType,
            couponWyuid: wyuid,
            couponSubType,
          };
        }
        callRentItemChange(record.indexId, {
          roomPriceList: (record.roomPriceList || []).map((item) => {
            if (item.rentDateId === rentDateId) {
              return { ...item, ...updateCouponInfo };
            }
            return item;
          }),
        });
      },
    });
  };

  const renderPriceItem: (
    dayPrice: ExchangeRoomPriceInfo,
    record: ExchangeRentRoomInfo,
  ) => React.ReactNode = (dayPrice, record) => {
    const isOriginRoom = record.itemRole === ExchangeRoomRole.OriginRoom; // 原房间
    const isTargetRoom = record.itemRole === ExchangeRoomRole.TargetRoom; // 新房间
    // 同价换房 新房间
    if (isSameType && isTargetRoom) return <CouponGapText text="—" />;
    const { inputPrice, couponValue, isBeforeAudit, priceChannel, couponNo } = dayPrice;
    let isInputDisabled = false;
    if (isOriginRoom || isSameType || isBeforeAudit) {
      // 原房间 或 同价换房 或 时间早已当前所在营业日 时 不可编辑价格
      isInputDisabled = true;
    }
    // 券是否可用
    const isCouponDisabled = couponService.isCouponDisabled({
      priceChannel,
      exclude: [couponNo],
    });
    return (
      <InputWithCoupon
        withCoupon
        value={inputPrice}
        couponValue={couponValue}
        inputDisabled={isInputDisabled}
        couponDisabled={isInputDisabled || isCouponDisabled}
        showCouponSelector={() => showCouponSelector(dayPrice, record)}
        onChange={(val) => handlePriceChange(val, dayPrice, record)}
      />
    );
  };

  const renderDepositItem: (deposit: string, record: ExchangeRentRoomInfo) => React.ReactNode = (
    deposit,
    record,
  ) => {
    const isOriginRoom = record.itemRole === ExchangeRoomRole.OriginRoom; // 原房间
    const isTargetRoom = record.itemRole === ExchangeRoomRole.TargetRoom; // 新房间
    // 同价换房 新房间
    if (isSameType && isTargetRoom) return '—';
    return (
      <InputWithCoupon
        withCoupon={false}
        inputDisabled={isOriginRoom || isSameType} // 同价换房 或者原房间 押金不可修改
        value={deposit}
        onChange={(val) => handleDepositChange(val, record)}
      />
    );
  };

  const showCardManage = () => {
    const originRoom = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom); // 原房间
    cardManageRef.current.showModal(
      {
        expectedArrivalTime: format2TimeStamp(rentTimeInfo.expectedArrivalTime),
        expectedDepartureTime: format2TimeStamp(rentTimeInfo.expectedArrivalTime),
        recNo: commonInfo.recNo,
        roomId: originRoom.roomId,
      },
      refreshRoomCardCount,
    );
  };

  const renderOperation: (indexId: string, record: ExchangeRentRoomInfo) => React.ReactNode = (
    indexId,
    record,
  ) => {
    if (record.itemRole === ExchangeRoomRole.OriginRoom) {
      return (
        <Button key="card" type="text" className={styles.textBtn} onClick={showCardManage}>
          {intl.get('drawer_exchange_table_content_card_manage')}
        </Button>
      );
    }
    return '—';
  };

  const { isMultiType, batchCouponEnable } = useMemo(() => {
    const originRoom = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom);
    const occTypeMap: Record<string, boolean> = {};
    let batchCouponStatus = false;
    (originRoom.roomPriceList || []).forEach((item) => {
      occTypeMap[item.occType] = true;
      // 未过夜审 且为线下渠道 可选券
      if (!item.isBeforeAudit && item.priceChannel === SourceChannelType.WALK_IN) {
        batchCouponStatus = true;
      }
    });
    return {
      // 多种入住类型
      isMultiType: Object.keys(occTypeMap).length > 1,
      // 是否可以批量选券
      batchCouponEnable: batchCouponStatus,
    };
  }, [rentRoomInfos]);

  const getTableInfo = () => {
    const leftColumns: ColumnsType<ExchangeRentRoomInfo> = [
      { render: renderRoomTag, width: 100, fixed: 'left', dataIndex: 'indexId', key: 'roomId' },
      {
        title: intl.get('app_constants_room_type'),
        dataIndex: 'roomTypeId',
        key: 'roomTypeId',
        width: 232,
        render: renderRoomType,
      },
      {
        title: intl.get('app_constants_room_no'),
        dataIndex: 'roomId',
        key: 'roomId',
        width: 232,
        render: renderRoomNo,
      },
      ...(cardEnabled
        ? [
            {
              title: intl.get('drawer_exchange_table_column_card_count'),
              dataIndex: 'roomCardCount',
              key: 'roomCardCount',
              width: 120,
              render: renderCardNum,
            },
          ]
        : []),
      {
        title: intl.get('app_reservation_deposit_amount'),
        dataIndex: 'deposit',
        key: 'deposit',
        align: 'right',
        width: 180,
        render: renderDepositItem,
      },
    ];
    const rightColumns: ColumnsType<ExchangeRentRoomInfo> = cardEnabled
      ? [
          {
            title: intl.get('app_table_column_action'),
            dataIndex: 'indexId',
            key: 'operation',
            align: 'right',
            width: 140,
            render: renderOperation,
          },
        ]
      : [];
    // 入住日期列
    const rentDayColumns: ColumnsType<ExchangeRentRoomInfo> = [];
    if (rentRoomInfos.length) {
      const { roomPriceList } = rentRoomInfos[0]; // 入住天价格列表
      const rentDays = roomPriceList || [];
      rentDays.forEach((rentDay, idx) => {
        const { rentDate } = rentDay;
        rentDayColumns.push({
          title: <CouponGapText text={rentDate} />,
          dataIndex: ['roomPriceList', idx],
          width: 260,
          align: 'right',
          render: (value, record) => renderPriceItem(value, record),
        });
      });
    }
    return {
      tableColumns: [...leftColumns, ...rentDayColumns, ...rightColumns],
    };
  };
  const tableInfo = getTableInfo();

  // 一键选券参数
  const getCouponParam = () => {
    return new Promise<IBatchCouponParams>((resolve) => {
      // 原房间
      const originRoom = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom);
      // 新房间
      const targetRoom = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.TargetRoom);

      const roomPriceList = []; // 可选券的房价列表
      targetRoom.roomPriceList.forEach((dayPrice) => {
        if (dayPrice.isBeforeAudit) return; // 已过夜审 不参与选券
        if (dayPrice.priceChannel !== SourceChannelType.WALK_IN) return; // 非线下渠道 不参与选券
        roomPriceList.push({
          key: dayPrice.rentDateId,
          occType: dayPrice.occType,
          rentDate: dayPrice.rentDate,
          roomPrice: dayPrice.inputPrice,
          couponNo: dayPrice.couponNo,
        });
      });

      const originCouponList: string[] = []; // 原房间券列表
      (originRoom.roomPriceList || []).forEach((item) => {
        if (!item.isBeforeAudit && item.couponNo) {
          originCouponList.push(item.couponNo);
        }
      });

      resolve({
        extra: {
          wyuid: ownerWYId,
          couponNos: originCouponList,
        },
        rooms: [
          {
            indexId: targetRoom.indexId,
            roomId: targetRoom.roomId,
            roomNo: targetRoom.roomNo,
            roomTypeId: targetRoom.roomTypeId,
            roomTypeName: targetRoom.roomTypeName,
            roomPriceList,
          },
        ],
      });
    });
  };

  return (
    <ContentBlock title={intl.get('reservation_room_block_title')}>
      <div className={styles.exchangeTypeWrapper}>
        <ExchangeRadio
          value={exchangeType}
          onChange={callExchangeTypeChange}
          isMultiType={isMultiType}
        />
        <div className={exchangeType === RoomExchangeType.KeepSame ? styles.blockHidden : null}>
          <BatchCouponButton
            userId={ownerWYId}
            channelType={batchCouponEnable ? SourceChannelType.WALK_IN : null}
            getCouponParam={getCouponParam}
            onChange={callBatchUpdateCoupon}
          />
        </div>
      </div>
      <div className={styles.rentRoomContainer}>
        <div className={styles.tableList}>
          <Table
            pagination={false}
            rowKey="indexId"
            columns={tableInfo.tableColumns}
            dataSource={rentRoomInfos}
            scroll={{ x: 800 }}
          />
        </div>
      </div>
      {/* 未还外设提示 */}
      {peripheralCount ? (
        <Alert
          message={`原房间已租借的 ${peripheralCount} 件外设，将转移至新房间`}
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      ) : null}
      <CardManage innerRef={cardManageRef} />
      <CouponSelector ref={couponSelectorRef} />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps, IProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
